/**
 * Purpose: Render the selected model domain grid as dense GeoJSON linework.
 * Assumptions: The selected domain is available in domainConfig and grid indices map through GridFactory.
 * External refs: src/external/weather-map-layer (vendored @openmeteo/weather-map-layer@0.0.19) GridFactory/GridData, react-map-gl/maplibre Source/Layer.
 */
import { GridFactory, type GridData } from "@openmeteo/weather-map-layer";
import type { Feature, FeatureCollection, LineString } from "geojson";
import { useMemo } from "react";
import { Layer, Source, type LayerProps } from "react-map-gl/maplibre";
import { getForecastDomainById } from "../domainConfig";

const DOMAIN_GRID_LAYER: LayerProps = {
  id: "domain-grid",
  type: "line",
  paint: {
    "line-color": "#111827",
    "line-width": 0.4,
    "line-opacity": 0.65,
  },
};

type DomainGridOverlayProps = {
  visible: boolean;
  domainId: string;
  beforeId?: string;
};

function normalizeLongitude(longitude: number) {
  return ((((longitude + 180) % 360) + 360) % 360) - 180;
}

function appendLineFeatures(
  features: Feature<LineString>[],
  grid: GridData,
  yStart: number,
  yEnd: number,
  xStart: number,
  xEnd: number,
) {
  const coordinates: [number, number][] = [];
  const rangeGrid = GridFactory.create(grid, [
    { start: yStart, end: yEnd },
    { start: xStart, end: xEnd },
  ]);

  rangeGrid.forEachPoint((point) => {
    coordinates.push([normalizeLongitude(point.lon), point.lat]);
  });

  if (coordinates.length > 1) {
    features.push({
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates,
      },
    });
  }
}

function buildRegularGridGeoJson(
  grid: Extract<GridData, { type: "regular" }>,
): FeatureCollection<LineString> {
  const features: Feature<LineString>[] = [];

  for (let y = 0; y <= grid.ny; y += 1) {
    const lat = grid.latMin + grid.dy * y;
    const coordinates: [number, number][] = [];
    for (let x = 0; x <= grid.nx; x += 1) {
      const lon = normalizeLongitude(grid.lonMin + grid.dx * x);
      coordinates.push([lon, lat]);
    }
    if (coordinates.length > 1) {
      features.push({
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates,
        },
      });
    }
  }

  for (let x = 0; x <= grid.nx; x += 1) {
    const lon = normalizeLongitude(grid.lonMin + grid.dx * x);
    const coordinates: [number, number][] = [];
    for (let y = 0; y <= grid.ny; y += 1) {
      const lat = grid.latMin + grid.dy * y;
      coordinates.push([lon, lat]);
    }
    if (coordinates.length > 1) {
      features.push({
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates,
        },
      });
    }
  }

  return {
    type: "FeatureCollection",
    features,
  };
}

function buildProjectedGridGeoJson(
  grid: Exclude<GridData, { type: "regular" }>,
): FeatureCollection<LineString> {
  const features: Feature<LineString>[] = [];

  for (let y = 0; y < grid.ny; y += 1) {
    appendLineFeatures(features, grid, y, y + 1, 0, grid.nx);
  }
  appendLineFeatures(features, grid, grid.ny - 1, grid.ny, 0, grid.nx);

  for (let x = 0; x < grid.nx; x += 1) {
    appendLineFeatures(features, grid, 0, grid.ny, x, x + 1);
  }
  appendLineFeatures(features, grid, 0, grid.ny, grid.nx - 1, grid.nx);

  return {
    type: "FeatureCollection",
    features,
  };
}

function buildDomainGridGeoJson(grid: GridData): FeatureCollection<LineString> {
  if (grid.type === "regular") {
    return buildRegularGridGeoJson(grid);
  }

  return buildProjectedGridGeoJson(grid);
}

export function DomainGridOverlay({
  visible,
  domainId,
  beforeId,
}: DomainGridOverlayProps) {
  const domain = useMemo(() => getForecastDomainById(domainId), [domainId]);
  const domainGridGeoJson = useMemo(
    () => buildDomainGridGeoJson(domain.grid),
    [domain.grid],
  );

  if (!visible) {
    return null;
  }

  return (
    <Source id="domain-grid-source" type="geojson" data={domainGridGeoJson}>
      <Layer {...DOMAIN_GRID_LAYER} beforeId={beforeId} />
    </Source>
  );
}
